import { type MouseEvent, type ReactNode, useEffect, useRef, useState } from 'react';

import { cn } from '@nesso/ui/lib/utils';
import { CardContent } from '@nesso/ui/primitives/card';

export const TruncatedContentCard = ({ children }: { children: ReactNode }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [hasOverflow, setHasOverflow] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const toggle = (e: MouseEvent) => {
        e.stopPropagation();
        setIsOpen((prev) => !prev);
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: re-run overflow check when children change — the wrapper has a fixed height, so ResizeObserver doesn't fire on content swaps
    useEffect(() => {
        const el = contentRef.current;
        if (!el || isOpen) return;

        const checkOverflow = () => {
            const isTruncated = el.scrollHeight > el.offsetHeight + 2;
            setHasOverflow(isTruncated);
        };

        checkOverflow();

        const resizeObserver = new ResizeObserver(checkOverflow);
        resizeObserver.observe(el);

        return () => resizeObserver.disconnect();
    }, [children, isOpen]);

    return (
        <CardContent
            className={cn(
                'bg-background-muted border border-muted rounded-sm py-6 text-sm',
                !isOpen && 'h-[13rem] md:h-[15.5rem]',
            )}
        >
            <div className="w-full h-full relative">
                <div ref={contentRef} className="h-full relative overflow-hidden">
                    {children}
                    {!isOpen && hasOverflow && (
                        <div className="w-full h-[3.75rem] absolute right-0 bottom-[-0.125rem] bg-gradient-to-b from-transparent to-background-muted before:absolute before:bottom-0 before:left-0 before:h-[1.25rem] before:w-full before:bg-background-muted before:content-['']" />
                    )}
                </div>
                {hasOverflow && (
                    <button
                        type="button"
                        className={cn(
                            'cursor-pointer block bg-background-muted underline underline-offset-2 decoration-1',
                            !isOpen && 'absolute bottom-0 left-0',
                        )}
                        aria-expanded={isOpen}
                        onClick={toggle}
                    >
                        {isOpen ? 'Less' : 'More'}
                    </button>
                )}
            </div>
        </CardContent>
    );
};
